import { CHART_COLORS } from "@/lib/constants";
import { formatCurrency, formatValue } from "@/lib/format-for-view";
import { format } from "date-fns";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import type { ChartData } from "../Consumption/ConsumBreakdownChart";

interface YearlyDonutChartProps {
	data: ChartData;
	year: string;
	viewMode: "kWh" | "Euro";
	startDate: Date;
	endDate: Date;
	lastYearStartDate: Date;
	lastYearEndDate: Date;
}

function CustomTooltip({
	active,
	payload,
	viewMode,
	totalValue,
	yearMode,
	year,
}: {
	active?: boolean;
	payload?: Array<{
		value?: number;
		name?: string;
		dataKey?: string;
		color?: string;
	}>;
	label?: string;
	viewMode: "kWh" | "Euro";
	totalValue?: number;
	yearMode?: boolean;
	year?: string;
}) {
	if (
		!active ||
		!payload ||
		payload.length === 0 ||
		payload[0] == null ||
		!totalValue
	) {
		return null;
	}

	let color = payload[0].color;
	if (!color) {
		if (yearMode) {
			if (year?.includes("2025")) {
				color =
					payload[0].name === "Heures Pleines"
						? CHART_COLORS.peak2025
						: CHART_COLORS.offPeak2025;
			} else {
				color =
					payload[0].name === "Heures Pleines"
						? CHART_COLORS.peak2024
						: CHART_COLORS.offPeak2024;
			}
		} else {
			color =
				payload[0].name === "Heures Pleines"
					? CHART_COLORS.peak
					: payload[0].name === "Heures Creuses"
						? CHART_COLORS.offPeak
						: CHART_COLORS.subscription;
		}
	}

	return (
		<div className="bg-white p-2 rounded shadow text-xs">
			<p className="font-semibold mb-1">{payload[0]?.name}</p>
			<div className="flex flex-col gap-1">
				{payload.map((entry, index) => (
					<div
						key={`${entry.name}-${index}`}
						className="flex items-center justify-between gap-2"
					>
						<div className="flex items-center">
							<div
								className="w-2 h-2 rounded-full mr-1"
								style={{ backgroundColor: entry.color }}
							/>
							<span>{entry.name}</span>
						</div>
						<span className="font-medium">
							{entry.name === "Abonnement"
								? formatCurrency(entry.value || 0)
								: formatValue(entry.value || 0, viewMode)}
						</span>
					</div>
				))}
				<div className="mt-1 pt-1 border-t border-gray-100">
					<span className="font-medium">
						Total: {formatValue(Number(totalValue), viewMode)}
					</span>
				</div>
			</div>
		</div>
	);
}

export default function YearlyDonutChart({
	data,
	year,
	viewMode,
	startDate,
	endDate,
	lastYearStartDate,
	lastYearEndDate,
}: YearlyDonutChartProps) {
	let chartData: { name: string; value: number; color: string }[] = [];
	let total = 0;

	// Set colors based on the year
	const isCurrentYear = new Date().getFullYear().toString() === year;
	const currentStartDate = isCurrentYear ? startDate : lastYearStartDate;
	const currentEndDate = isCurrentYear ? endDate : lastYearEndDate;

	const yearColors = isCurrentYear
		? {
				offPeak: CHART_COLORS.offPeak2025, // Light pink for 2025 off-peak
				peak: CHART_COLORS.peak2025, // Pink for 2025 peak
				subscription: CHART_COLORS.subscription, // Navy blue
			}
		: {
				offPeak: CHART_COLORS.offPeak2024, // Light turquoise for 2024 off-peak
				peak: CHART_COLORS.peak2024, // Turquoise for 2024 peak
				subscription: CHART_COLORS.subscription, // Navy blue
			};

	// In the YearlyDonutChart component, update the chartData creation:
	if (viewMode === "kWh") {
		total = data.offPeak + data.peak;
		chartData = [
			{
				name: "Heures Creuses",
				value: data.offPeak,
				color: yearColors.offPeak,
			},
			{ name: "Heures Pleines", value: data.peak, color: yearColors.peak },
		];
	} else {
		const offPeakEuro = data.offPeak * 0.18;
		const peakEuro = data.peak * 0.18;
		const subscriptionValue = data.subscription || 0;
		total = offPeakEuro + peakEuro + subscriptionValue;

		chartData = [
			{
				name: "Heures Creuses",
				value: offPeakEuro,
				color: yearColors.offPeak,
			},
			{ name: "Heures Pleines", value: peakEuro, color: yearColors.peak },
			{
				name: "Abonnement",
				value: subscriptionValue,
				color: CHART_COLORS.subscription,
			},
		];
	}

	return (
		<div className="flex flex-col items-center justify-center">
			<PieChart width={160} height={160}>
				<Pie
					data={chartData}
					cx="50%"
					cy="50%"
					innerRadius={45}
					outerRadius={60}
					fill="#8884d8"
					dataKey="value"
					startAngle={90}
					endAngle={-270}
				>
					{chartData.map((entry, index) => (
						<Cell
							key={`cell-${entry.name}-${index}`}
							fill={entry.color}
							style={{ outline: "none" }}
						/>
					))}
				</Pie>
				<Tooltip
					content={
						<CustomTooltip
							viewMode={viewMode}
							totalValue={total}
							yearMode={true}
							year={year}
						/>
					}
					isAnimationActive={false}
				/>
			</PieChart>
			<p className="text-xs text-gray-600 mt-2">
				{`01/01/${format(currentStartDate, "yyyy")} -${format(currentEndDate, "dd/MM/yyyy")}`}
			</p>
		</div>
	);
}
